import { createContext, useContext, useEffect, useMemo } from "react"
import type { StoreApi } from "zustand"
import { useEngine } from "hooks/api/useEngine"
import { useAsync } from "hooks/core/useAsync"
import { isDataTile } from "features/Dashboard/types/dashboardDocument"
import {
  DashboardState,
  selectActivePage,
  useDashboardStore,
  useDashboardStoreApi,
} from "./dashboardStore"
import { createEngineRunner, RunScript } from "./engineRunner"
import { QueryQueue, SupersededRunError } from "./queryQueue"
import { runTile } from "./runTile"

/** Whether the session of `generation` still takes calls: not moved on, and ready. */
export const sessionIsLive = (
  store: StoreApi<DashboardState>,
  generation: number
): boolean => {
  const { session } = store.getState()
  return session.generation === generation && session.status === "ready"
}

/**
 * The engine runner for one session generation. It refuses to send a script
 * once the session has moved on (a restart bumped the generation, or one is
 * under way), so a superseded queue cannot run into the next session.
 */
export const createSessionRunner = (
  store: StoreApi<DashboardState>,
  generation: number,
  deps: Parameters<typeof createEngineRunner>[0]
): RunScript =>
  createEngineRunner({
    ...deps,
    run: async (script) => {
      if (!sessionIsLive(store, generation)) {
        throw new SupersededRunError()
      }
      return deps.run(script)
    },
  })

/** One queue per session instance and generation; a RESET starts a fresh one. */
export const useDashboardQueue = (): QueryQueue | null => {
  const store = useDashboardStoreApi()
  const instance = useDashboardStore((s) => s.session.instance)
  const generation = useDashboardStore((s) => s.session.generation)
  const { RunEngineQuery } = useEngine()
  const { pollingAsync, getAsyncResult } = useAsync()
  const queue = useMemo(() => {
    if (!instance) return null
    const run = createSessionRunner(store, generation, {
      run: async (script) => {
        const r = await RunEngineQuery(instance, script, true)
        return { status: r.status, data: r.data }
      },
      poll: async (id) => ({ status: (await pollingAsync(id)).status }),
      fetch: async (id) => ({ data: (await getAsyncResult(id)).data }),
    })
    // A read of the lineage waits its turn here too, and runs only while the
    // session is ready: its RESET and preparation run outside the queue.
    return new QueryQueue(run, () => sessionIsLive(store, generation))
    // `generation` is in the key on purpose: a RESET invalidates cached
    // results, so it gets a fresh queue. The engine commands are stable.
  }, [store, instance, generation, RunEngineQuery, pollingAsync, getAsyncResult])

  // A replaced queue, and the last one on unmount, stop sending. The queue
  // is built in render, not in this effect, so the tile runner gets the new
  // one in the same commit. It is always null at mount (a fresh store has
  // no instance), so StrictMode's mount-time cleanup never disposes a live
  // queue.
  useEffect(() => () => queue?.dispose(), [queue])

  return queue
}

/**
 * The dashboard's queue, for what a tile sends outside its own run (the
 * check of a traced cell, the read of the session's lineage). Null outside
 * a dashboard and without a session.
 */
export const DashboardQueueContext = createContext<QueryQueue | null>(null)
export const useDashboardQueueContext = (): QueryQueue | null =>
  useContext(DashboardQueueContext)

/** Runs the stale data tiles of the active page while the session is ready. */
export const useTileRunner = (queue: QueryQueue | null): void => {
  const store = useDashboardStoreApi()
  const status = useDashboardStore((s) => s.session.status)
  const tiles = useDashboardStore((s) => s.tiles)
  const activePageId = useDashboardStore((s) => s.activePageId)

  useEffect(() => {
    if (!queue || status !== "ready") return
    const page = selectActivePage(store.getState())
    if (!page) return
    page.tiles.filter(isDataTile).forEach((tile) => {
      if (tiles[tile.id]?.status !== "stale") return
      // runTile marks the tile queued before it awaits, so the re-render this
      // causes does not start it twice.
      void runTile(store, queue, tile.id)
    })
  }, [queue, status, tiles, activePageId, store])
}
